import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, ShieldCheck, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Tchat sécurisé d'un match FodindinLove : ouvert uniquement entre abonnés
 *  Love qui se sont mutuellement aimés. Les coordonnées y sont bloquées. */
export const LoveTchat = ({ moiId, profil, onFermer }) => {
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [texte, setTexte] = useState("");
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const basRef = useRef(null);

  useEffect(() => {
    let vivant = true;
    axios
      .post(`${API}/love/conversation`, { profile_id: moiId, autre_id: profil.id })
      .then((r) => vivant && setConv(r.data))
      .catch((e) =>
        vivant && setErreur(e.response?.data?.detail?.message || "Tchat indisponible."),
      );
    return () => { vivant = false; };
  }, [moiId, profil.id]);

  useEffect(() => {
    if (!conv) return;
    const charger = () =>
      axios
        .get(`${API}/love/conversation/${conv.id}/messages`, { params: { profile_id: moiId } })
        .then((r) => setMessages(r.data))
        .catch(() => {});
    charger();
    const minuteur = setInterval(charger, 4000);
    return () => clearInterval(minuteur);
  }, [conv?.id, moiId]);

  useEffect(() => { basRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const envoyer = async (e) => {
    e.preventDefault();
    const contenu = texte.trim();
    if (!contenu || !conv) return;
    setEnvoi(true);
    try {
      const r = await axios.post(`${API}/love/conversation/${conv.id}/messages`, {
        sender_id: moiId, text: contenu,
      });
      setMessages((m) => [...m, r.data]);
      setTexte("");
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || "Message non envoyé.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <motion.aside
      initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 40 }}
      data-testid="love-tchat"
      className="fixed inset-x-0 bottom-0 z-50 flex h-[78vh] flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl ring-1 ring-stone-900/10 sm:inset-y-0 sm:left-auto sm:right-0 sm:h-full sm:w-[420px] sm:rounded-none"
    >
      <header className="flex items-center justify-between gap-3 border-b border-stone-100 bg-[#2F363F] px-5 py-4 text-white">
        <div>
          <p className="font-heading text-lg font-extrabold">{profil.firstname}</p>
          <p className="inline-flex items-center gap-1 text-[11px] text-white/70">
            <ShieldCheck size={12} /> Tchat sécurisé — coordonnées bloquées
          </p>
        </div>
        <button onClick={onFermer} data-testid="love-tchat-fermer"
          className="rounded-full p-2 transition-colors hover:bg-white/10">
          <X size={18} />
        </button>
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto bg-[#FAF8F5] px-4 py-5"
        data-testid="love-tchat-messages">
        {erreur ? (
          <p className="rounded-2xl bg-red-50 p-4 text-sm font-semibold text-red-600"
            data-testid="love-tchat-erreur">{erreur}</p>
        ) : !conv ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#FF4757]" size={26} /></div>
        ) : messages.length === 0 ? (
          <p className="py-10 text-center text-sm text-stone-500" data-testid="love-tchat-vide">
            C'est un match ! Lancez la conversation avec un vrai bonjour.
          </p>
        ) : (
          messages.map((m) => (
            <div key={m.id} data-testid={`love-tchat-message-${m.id}`}
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                m.sender_id === moiId
                  ? "ml-auto bg-[#FF4757] text-white"
                  : "bg-white text-stone-700 ring-1 ring-stone-900/5"
              }`}>
              {m.text}
            </div>
          ))
        )}
        <div ref={basRef} />
      </div>

      {!erreur && (
        <form onSubmit={envoyer} className="flex items-center gap-2 border-t border-stone-100 bg-white px-4 py-3">
          <input value={texte} onChange={(e) => setTexte(e.target.value)}
            placeholder="Votre message..." maxLength={1000} data-testid="love-tchat-input"
            className="flex-1 rounded-full bg-stone-100 px-4 py-3 text-sm outline-none ring-[#FF4757] focus:ring-2" />
          <button type="submit" disabled={envoi || !texte.trim()} data-testid="love-tchat-envoyer"
            className="rounded-full bg-[#FF4757] p-3 text-white transition-transform hover:scale-105 disabled:opacity-40">
            {envoi ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </form>
      )}
    </motion.aside>
  );
};
